import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createSubGroup, updateSubGroup, fetchGroupCategories, fetchPrimaryGroups, fetchSubGroupNextSequencePreview } from '../../store/coaStore/coaThunk';
import { ApiStatus } from '../../utils/api.types';
import { ISubGroup } from '../../utils/coa.types';
import { toast } from 'react-toastify';

interface SubGroupFormProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  editData?: ISubGroup | null;
}

const schema = yup.object().shape({
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters').max(100, 'Name must not exceed 100 characters'),
  primaryGroup: yup.string().required('Primary Group is required'),
});

const SubGroupForm: FC<SubGroupFormProps> = ({ open, onClose, editData }) => {
  const dispatch = useAppDispatch();
  const { groupCategories, primaryGroups, status } = useAppSelector(state => state.coa);
  const [selectedGroupCategory, setSelectedGroupCategory] = useState('');
  const [selectedPrimaryGroup, setSelectedPrimaryGroup] = useState('');
  const [filteredPrimaryGroups, setFilteredPrimaryGroups] = useState(primaryGroups);
  const [previewAccountingCode, setPreviewAccountingCode] = useState('Auto');

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      primaryGroup: '',
    }
  });

  const { handleSubmit, reset, formState: { errors } } = methods;

  useEffect(() => {
    if (open) {
      dispatch(fetchGroupCategories({ limit: 100 }));
      dispatch(fetchPrimaryGroups({ limit: 100 }));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (selectedGroupCategory) {
      const filtered = primaryGroups.filter((pg: any) => {
        const categoryId = typeof pg.groupCategory === 'object' ? pg.groupCategory?._id : pg.groupCategory;
        return categoryId === selectedGroupCategory;
      });
      setFilteredPrimaryGroups(filtered);
    } else {
      setFilteredPrimaryGroups(primaryGroups);
    }
  }, [selectedGroupCategory, primaryGroups]);

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      const categoryId = typeof editData.groupCategory === 'object'
        ? (editData.groupCategory as any)?._id
        : editData.groupCategory;

      const primaryId = typeof editData.primaryGroup === 'object'
        ? (editData.primaryGroup as any)?._id
        : editData.primaryGroup;

      reset({
        name: editData.name || '',
        primaryGroup: primaryId || '',
      });
      setSelectedGroupCategory(categoryId || '');
      setSelectedPrimaryGroup(primaryId || '');
      setPreviewAccountingCode(editData.accountingCode || 'Auto');
    } else {
      reset({
        name: '',
        primaryGroup: '',
      });
      setSelectedGroupCategory('');
      setSelectedPrimaryGroup('');
      setPreviewAccountingCode('Auto');
    }
  }, [editData, reset, open]);

  // Fetch next sequence when primary group changes (only for new entries)
  useEffect(() => {
    if (!editData && selectedPrimaryGroup) {
      dispatch(fetchSubGroupNextSequencePreview(selectedPrimaryGroup))
        .unwrap()
        .then((result) => {
          setPreviewAccountingCode(result.accountingCode);
        })
        .catch(() => {
          setPreviewAccountingCode('Auto');
        });
    }
  }, [selectedPrimaryGroup, editData, dispatch]);

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        ...data,
        groupCategory: selectedGroupCategory,
      };

      if (editData?._id) {
        await dispatch(updateSubGroup({ id: editData._id, payload })).unwrap();
      } else {
        await dispatch(createSubGroup(payload)).unwrap();
      }
      reset();
      onClose(true);
    } catch (error: any) {
      toast.error(error?.message || 'Something went wrong');
    }
  };

  const handleClose = () => {
    reset();
    setSelectedGroupCategory('');
    setSelectedPrimaryGroup('');
    setPreviewAccountingCode('Auto');
    onClose(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 550 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {editData ? 'Edit Sub Group' : 'Add Sub Group'}
            </Typography>
          </Box>

          {/* Content - Scrollable */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Group Category *</InputLabel>
                  <Select
                    value={selectedGroupCategory}
                    label="Group Category *"
                    onChange={(e: SelectChangeEvent) => {
                      setSelectedGroupCategory(e.target.value);
                      setSelectedPrimaryGroup('');
                      methods.setValue('primaryGroup', '');
                      setPreviewAccountingCode('Auto');
                    }}
                  >
                    {groupCategories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name} {cat.accountingCode ? `(${cat.accountingCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="primaryGroup"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.primaryGroup} size="small">
                      <InputLabel>Primary Group *</InputLabel>
                      <Select
                        {...field}
                        label="Primary Group *"
                        value={selectedPrimaryGroup}
                        disabled={!selectedGroupCategory}
                        onChange={(e: SelectChangeEvent) => {
                          setSelectedPrimaryGroup(e.target.value);
                          field.onChange(e.target.value);
                        }}
                      >
                        {filteredPrimaryGroups.map((pg: any) => (
                          <MenuItem key={pg._id} value={pg._id}>
                            {pg.name} {pg.accountingCode ? `(${pg.accountingCode})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                      {!selectedGroupCategory && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Please select a Group Category first
                        </Typography>
                      )}
                      {errors.primaryGroup && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.primaryGroup.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Name"
                      fullWidth
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      placeholder="e.g., Buildings, Machinery, Vehicles"
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontStyle: 'italic'
                  }}
                >
                  Sequence number and accounting code will be auto-generated upon saving.
                </Typography>
                {previewAccountingCode !== 'Auto' && (
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 600,
                      mt: 0.5,
                      fontSize: '1rem'
                    }}
                  >
                    Accounting Code: {previewAccountingCode}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Footer - Fixed at bottom */}
          <Box sx={{
            borderTop: '1px solid #e5e7eb',
            p: 2,
            backgroundColor: '#f9fafb',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={status === ApiStatus.LOADING}
              sx={{
                minWidth: 100,
                height: 40,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{
                minWidth: 140,
                height: 40,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {editData ? 'Update' : 'Save'}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default SubGroupForm;
